import { useEffect, useState } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import { fetchGlobalOrders } from "@/services/superadminApi";
import Loader from "@/components/ui/Loader";
import {
    SectionHeader,
    Badge,
    CardGlass,
    TablePro,
    StatCard,
    Button
} from "@/components/ui/PremiumComponents";
import { ShoppingBag, Clock, MapPin, Truck, ChevronRight, Filter, Search } from 'lucide-react';

export default function SuperAdminOrdersGlobal() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        fetchGlobalOrders()
            .then(res => setOrders(res.items || []))
            .catch(() => setError("Error al sincronizar pedidos globales."))
            .finally(() => setLoading(false));
    }, []);

    const kpis = [
        { label: "Pedidos Totales", value: orders.length, delta: 12, trend: 'up', icon: <ShoppingBag size={22} />, color: 'var(--primary)' },
        { label: "En Cocina", value: orders.filter(o => o.status === 'preparing').length, delta: 5, trend: 'up', icon: <Clock size={22} />, color: 'var(--accent)' },
        { label: "En Reparto", value: orders.filter(o => o.status === 'delivering').length, delta: 8, trend: 'up', icon: <Truck size={22} />, color: 'var(--secondary)' },
        { label: "Completados", value: orders.filter(o => o.status === 'delivered').length, delta: 24, trend: 'up', icon: <ShoppingBag size={22} />, color: 'var(--success)' },
    ];

    const renderRow = (row) => {
        const status = row.status || "preparing";
        return (
            <>
                <td className="px-6 py-5">
                    <div className="flex flex-col">
                        <span className="font-black text-white text-base tracking-tighter">#{row.order_id || row.id}</span>
                        <span className="text-[10px] font-black uppercase tracking-widest mt-0.5" style={{ color: 'var(--primary)' }}>{row.company_id}</span>
                    </div>
                </td>
                <td className="px-6 py-5">
                    <div className="flex flex-col">
                        <span className="font-bold text-white text-sm">{row.customer_name || "Invitado"}</span>
                        {row.customer_phone && <span className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>{row.customer_phone}</span>}
                    </div>
                </td>
                <td className="px-6 py-5">
                    <Badge status={
                        status === 'delivered' ? 'success' :
                            status === 'delivering' ? 'secondary' :
                                status === 'cancelled' ? 'danger' : 'warning'
                    }>
                        {status}
                    </Badge>
                </td>
                <td className="px-6 py-5">
                    <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--text-soft)' }}>
                        <MapPin size={14} style={{ color: 'var(--text-muted)' }} />
                        <span className="truncate max-w-[150px]">{row.delivery_address || "En local / Recogida"}</span>
                    </div>
                </td>
                <td className="px-6 py-5">
                    <div className="flex flex-col items-end">
                        <span className="font-black text-white text-base">€{row.total_price?.toFixed(2) || "0.00"}</span>
                        <span className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>{row.payment_method?.toUpperCase()}</span>
                    </div>
                </td>
                <td className="px-6 py-5 text-right">
                    <Button variant="secondary" className="px-3 py-2 text-xs">
                        <ChevronRight size={16} />
                    </Button>
                </td>
            </>
        );
    };

    return (
        <SuperAdminLayout>
            <div className="page-fade-in">
                <SectionHeader
                    title="Monitor de Pedidos"
                    subtitle="Flujo de transacciones y estados logísticos en tiempo real de todo el ecosistema."
                    actions={
                        <div className="flex gap-3">
                            <div className="flex -space-x-3 overflow-hidden p-1">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="inline-block h-8 w-8 rounded-full ring-2 flex items-center justify-center text-[10px] font-black text-primary" style={{ backgroundColor: 'rgb(59 130 246 / 0.2)', ringColor: 'var(--bg-deep)' }}>OP</div>
                                ))}
                            </div>
                            <Button className="flex items-center gap-2"><Filter size={16} /> Filtros</Button>
                        </div>
                    }
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                    {kpis.map((kpi, i) => (
                        <StatCard key={i} {...kpi} />
                    ))}
                </div>

                {error && <div className="bg-danger/10 border border-danger/20 p-4 rounded-xl text-danger mb-8 font-bold">⚠️ {error}</div>}

                <div className="space-y-6">
                    <div className="flex flex-col md:flex-row gap-4 items-center mb-6">
                        <div className="relative flex-1 w-full group">
                            <Search
                                size={18}
                                className="absolute left-4 top-1/2 -translate-y-1/2 group-focus-within:text-primary transition-colors"
                                style={{ color: 'var(--text-muted)' }}
                            />
                            <input
                                type="text"
                                placeholder="Buscar por ID de pedido, cliente o restaurante..."
                                className="w-full bg-white/5 border border-white/10 rounded-2xl py-3.5 pl-12 pr-4 text-sm text-white outline-none focus:ring-2 ring-primary/20 transition-all font-medium"
                            />
                        </div>
                    </div>

                    <TablePro
                        headers={["ID / Tenant", "Comensal", "Estado", "Dirección / Zona", "Importe", ""]}
                        data={orders}
                        loading={loading}
                        renderRow={renderRow}
                    />
                </div>
            </div>
        </SuperAdminLayout>
    );
}
